import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, apiErrorText, formatBRL, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CheckCircle } from "@phosphor-icons/react";

const STATUS = {
  pending: { label: "Pendente", cls: "bg-accent/20 text-accent" },
  paid: { label: "Pago", cls: "bg-green-500/20 text-green-400" },
  overdue: { label: "Vencido", cls: "bg-destructive/20 text-destructive" },
  cancelled: { label: "Cancelado", cls: "bg-muted text-muted-foreground" },
};

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  client_id: "", inspection_id: "", number: "", description: "",
  amount: "", issue_date: today(), due_date: today(),
  status: "pending", payment_method: "", notes: "",
};

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const [i, c, ins] = await Promise.all([
      api.get("/invoices", { params: filter ? { status: filter } : {} }),
      api.get("/clients"),
      api.get("/inspections"),
    ]);
    setRows(i.data); setClients(c.data); setInspections(ins.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, amount: r.amount, issue_date: r.issue_date?.slice(0, 10), due_date: r.due_date?.slice(0, 10) }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.inspection_id) payload.inspection_id = null;
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Fatura salva"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const remove = async (id) => { if (!window.confirm("Remover fatura?")) return; await api.delete(`/invoices/${id}`); toast.success("Removida"); load(); };
  const markPaid = async (id) => { await api.post(`/invoices/${id}/mark-paid`); toast.success("Marcada como paga"); load(); };
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <Layout
      title="Faturamento"
      subtitle="Registro interno de faturas, cobranças e recebimentos"
      actions={<Button onClick={openNew} data-testid="new-invoice-btn" className="gap-2"><Plus size={16} weight="bold" /> Nova fatura</Button>}
    >
      <div className="mb-6 flex items-center gap-3">
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue placeholder="Filtrar por status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="tech-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="label-tech text-muted-foreground p-4">Nº</th>
              <th className="label-tech text-muted-foreground p-4">Cliente</th>
              <th className="label-tech text-muted-foreground p-4">Descrição</th>
              <th className="label-tech text-muted-foreground p-4">Emissão</th>
              <th className="label-tech text-muted-foreground p-4">Vencimento</th>
              <th className="label-tech text-muted-foreground p-4 text-right">Valor</th>
              <th className="label-tech text-muted-foreground p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="invoices-list">
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">Nenhuma fatura</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-secondary/30">
                <td className="p-4 mono text-sm">{r.number || "—"}</td>
                <td className="p-4">{clientName(r.client_id)}</td>
                <td className="p-4 text-sm">{r.description}</td>
                <td className="p-4 text-sm mono">{formatDate(r.issue_date)}</td>
                <td className="p-4 text-sm mono">{formatDate(r.due_date)}</td>
                <td className="p-4 text-right mono font-bold">{formatBRL(r.amount)}</td>
                <td className="p-4"><Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label}</Badge></td>
                <td className="p-4">
                  <div className="flex justify-end gap-1">
                    {r.status === "pending" && (
                      <Button size="icon" variant="ghost" onClick={() => markPaid(r.id)} title="Marcar como paga" data-testid={`pay-invoice-${r.id}`}>
                        <CheckCircle size={16} className="text-green-400" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash size={16} className="text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar fatura" : "Nova fatura"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-tech">Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Inspeção (opcional)</Label>
              <Select value={form.inspection_id || "none"} onValueChange={(v) => setForm({ ...form, inspection_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {inspections.filter((i) => !form.client_id || i.client_id === form.client_id).map((i) => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Descrição *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-description" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Valor (R$) *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount" />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Emissão</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Vencimento</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Forma de pagamento</Label>
              <Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="PIX, transferência, boleto..." />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-invoice-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
